import { v4 as uuidv4 } from 'uuid';
import {
  SOUNDPROOFING_GLASS_KINDS,
  SOUNDPROOFING_LAYER_TYPES,
  SOUNDPROOFING_MAX_LAYERS_COUNT,
  SOUNDPROOFING_PRODUCT_TYPES,
  SOUNDPROOFING_RANGE_KEYS,
  SOUNDPROOFING_RANGE_LIMITS,
  SOUNDPROOFING_THICKNESS_OPTIONS,
} from '../constants/soundproofing';

export const RANGE_ERROR_TYPES = {
  min: 'min',
  max: 'max',
  range: 'range',
};

const MOCK_RESULTS_COUNT = 120;

function createSeededRandom(seed) {
  let value = seed;
  return () => {
    value = (value * 16807) % 2147483647;
    return (value - 1) / 2147483646;
  };
}

function toNumberOrNull(value) {
  if (value === '' || value === null || value === undefined) return null;
  const number = Number(String(value).replace(',', '.'));
  return Number.isNaN(number) ? NaN : number;
}

class SoundproofingServices {
  static mockResults = null;

  /* Layers Start */

  static getDefaultLayerParams = (type) => {
    if (type === SOUNDPROOFING_LAYER_TYPES.glass) {
      return {
        [SOUNDPROOFING_GLASS_KINDS.single]: { checked: false, thickness: null },
        [SOUNDPROOFING_GLASS_KINDS.laminated]: {
          checked: false, thickness: null, pvb: false, acousticPvb: false,
        },
      };
    }
    return { thickness: null };
  };

  static createLayer = (type) => ({
    id: uuidv4(),
    type,
    params: SoundproofingServices.getDefaultLayerParams(type),
  });

  static getInitialLayers = () => [SoundproofingServices.createLayer(SOUNDPROOFING_LAYER_TYPES.glass)];

  static isLayerFilled = (layer) => {
    if (!layer) return false;
    if (layer.type === SOUNDPROOFING_LAYER_TYPES.frame) {
      return layer.params.thickness !== null;
    }
    return Object.values(layer.params).some((kind) => Object.values(kind).some((value) => value !== null && value !== false));
  };

  static canAddLayers = (layers) => layers.length + 2 <= SOUNDPROOFING_MAX_LAYERS_COUNT;

  static addLayers = (layers) => {
    if (!SoundproofingServices.canAddLayers(layers)) return layers;
    return [
      ...layers,
      SoundproofingServices.createLayer(SOUNDPROOFING_LAYER_TYPES.frame),
      SoundproofingServices.createLayer(SOUNDPROOFING_LAYER_TYPES.glass),
    ];
  };

  // Every glass except the first one can be removed together with the frame before it
  static canRemoveLayer = (layers, index) => index > 0 && layers[index]?.type === SOUNDPROOFING_LAYER_TYPES.glass;

  static removeLayer = (layers, index) => {
    if (!SoundproofingServices.canRemoveLayer(layers, index)) return layers;
    return layers.filter((_, i) => i !== index && i !== index - 1);
  };

  /* Layers End */

  /* Parameters Start */

  static getDefaultProductTypes = () => Object.values(SOUNDPROOFING_PRODUCT_TYPES)
    .reduce((acc, key) => ({ ...acc, [key]: false }), {});

  static getDefaultRanges = () => Object.entries(SOUNDPROOFING_RANGE_LIMITS)
    .reduce((acc, [key, { min, max }]) => ({ ...acc, [key]: [min, max] }), {});

  static areRangesChanged = (ranges) => Object.entries(SOUNDPROOFING_RANGE_LIMITS)
    .some(([key, { min, max }]) => ranges[key][0] !== min || ranges[key][1] !== max);

  static isCompositionSet = ({ layers, productTypes, ranges }) => (
    layers.some(SoundproofingServices.isLayerFilled)
    || Object.values(productTypes).some(Boolean)
    || SoundproofingServices.areRangesChanged(ranges)
  );

  /* Parameters End */

  /* Results Filters Start */

  static getDefaultResultsFilters = () => ({
    composition: '',
    thickness: null,
    [SOUNDPROOFING_RANGE_KEYS.rw]: { from: '', to: '' },
    [SOUNDPROOFING_RANGE_KEYS.rwC]: { from: '', to: '' },
    [SOUNDPROOFING_RANGE_KEYS.rwCtr]: { from: '', to: '' },
  });

  static sanitizeNumberInput = (value) => {
    const normalized = String(value).replace(',', '.').replace(/[^\d.]/g, '');
    const [integer, ...fraction] = normalized.split('.');
    return fraction.length ? `${integer}.${fraction.join('')}` : integer;
  };

  // Returns which of the inputs are invalid and what kind of hint should be shown
  static validateFilterRange = ({ from, to }, { min, max }) => {
    const fromNumber = toNumberOrNull(from);
    const toNumber = toNumberOrNull(to);

    const isFromInvalid = fromNumber !== null && (Number.isNaN(fromNumber) || fromNumber < min || fromNumber > max);
    const isToInvalid = toNumber !== null && (Number.isNaN(toNumber) || toNumber < min || toNumber > max);
    const isOrderInvalid = !isFromInvalid && !isToInvalid && fromNumber !== null && toNumber !== null && fromNumber > toNumber;

    if ((isFromInvalid && isToInvalid) || isOrderInvalid) {
      return { from: true, to: true, type: RANGE_ERROR_TYPES.range };
    }
    if (isFromInvalid) {
      return { from: true, to: false, type: Number.isNaN(fromNumber) ? RANGE_ERROR_TYPES.range : fromNumber < min ? RANGE_ERROR_TYPES.min : RANGE_ERROR_TYPES.max };
    }
    if (isToInvalid) {
      return { from: false, to: true, type: Number.isNaN(toNumber) ? RANGE_ERROR_TYPES.range : toNumber > max ? RANGE_ERROR_TYPES.max : RANGE_ERROR_TYPES.min };
    }
    return null;
  };

  static getResultsFiltersErrors = (filters) => [SOUNDPROOFING_RANGE_KEYS.rw, SOUNDPROOFING_RANGE_KEYS.rwC, SOUNDPROOFING_RANGE_KEYS.rwCtr]
    .reduce((acc, key) => {
      const error = SoundproofingServices.validateFilterRange(filters[key], SOUNDPROOFING_RANGE_LIMITS[key]);
      return error ? { ...acc, [key]: error } : acc;
    }, {});

  static areResultsFiltersChanged = (filters) => JSON.stringify(filters) !== JSON.stringify(SoundproofingServices.getDefaultResultsFilters());

  /* Results Filters End */

  /* Mocks Start */

  // TODO: remove when the API is ready
  static getMockResultsList = () => {
    if (SoundproofingServices.mockResults) return SoundproofingServices.mockResults;

    const random = createSeededRandom(42);
    const pick = (list) => list[Math.floor(random() * list.length)];
    const between = (min, max) => Math.round(min + random() * (max - min));
    const glasses = ['4', '6', '8', '10', '44.2', '66.4ac', '1212.4ac', 'Stratobel Clear 22.2'];
    const frames = ['6', '12', '16', '18', '24'];

    SoundproofingServices.mockResults = Array.from({ length: MOCK_RESULTS_COUNT }, (_, i) => {
      const isDoubleChamber = random() > 0.4;
      const parts = isDoubleChamber
        ? [pick(glasses), pick(frames), pick(glasses), pick(frames), pick(glasses)]
        : [pick(glasses), pick(frames), pick(glasses)];

      return {
        id: i + 1,
        composition: parts.join(' - '),
        thickness: pick(SOUNDPROOFING_THICKNESS_OPTIONS.results),
        rw: between(28, 45),
        rwC: between(5, 10),
        rwCtr: between(25, 40),
      };
    });

    return SoundproofingServices.mockResults;
  };

  // TODO: remove when the API is ready, the filtering has to be done on the backend
  static getMockResults = ({
    query, filters, page, pageSize,
  }) => {
    const isInRange = (value, [min, max]) => value >= min && value <= max;
    const isInFilter = (value, { from, to }) => {
      const fromNumber = toNumberOrNull(from);
      const toNumber = toNumberOrNull(to);
      return (fromNumber === null || value >= fromNumber) && (toNumber === null || value <= toNumber);
    };
    const compositionQuery = filters.composition.trim().toLowerCase();

    const filtered = SoundproofingServices.getMockResultsList().filter((item) => (
      isInRange(+item.thickness, query.ranges[SOUNDPROOFING_RANGE_KEYS.thickness])
      && isInRange(item.rw, query.ranges[SOUNDPROOFING_RANGE_KEYS.rw])
      && isInRange(item.rwC, query.ranges[SOUNDPROOFING_RANGE_KEYS.rwC])
      && isInRange(item.rwCtr, query.ranges[SOUNDPROOFING_RANGE_KEYS.rwCtr])
      && (!compositionQuery || item.composition.toLowerCase().includes(compositionQuery))
      && (!filters.thickness || item.thickness === filters.thickness)
      && isInFilter(item.rw, filters[SOUNDPROOFING_RANGE_KEYS.rw])
      && isInFilter(item.rwC, filters[SOUNDPROOFING_RANGE_KEYS.rwC])
      && isInFilter(item.rwCtr, filters[SOUNDPROOFING_RANGE_KEYS.rwCtr])
    ));

    return {
      items: filtered.slice((page - 1) * pageSize, page * pageSize),
      total: filtered.length,
    };
  };

  /* Mocks End */
}

export default SoundproofingServices;
